import { ArrowRight, Instagram, Linkedin, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import queueOSLogoDark from "@/assets/queueos-logo-dark.svg";
import queueOSLogoLight from "@/assets/queueos-logo-light.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";

export const Footer = () => {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const { scrollToAnchor } = useSmoothScroll();

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, anchorId: string) => {
    e.preventDefault();
    
    // If we're not on the home page, navigate there first
    if (location.pathname !== "/") {
      navigate("/");
      // Wait for navigation to complete, then scroll
      setTimeout(() => scrollToAnchor(anchorId), 100);
    } else {
      scrollToAnchor(anchorId);
    }
  };
  
  const showCTA = location.pathname !== "/student" && location.pathname !== "/deanza" && location.pathname !== "/whyqueueos";

  return <footer className="bg-foreground text-background py-16 relative z-50 isolate">
      <div className="container mx-auto px-6">
        {/* CTA Section */}
        {showCTA && <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to transform your
            <span className="bg-gradient-primary bg-clip-text text-transparent"> workflow?</span>
          </h2>
          <p className="text-xl text-background/80 max-w-2xl mx-auto mb-8">Join teams already using queueOS to streamline their task management and boost productivity.</p>
          <Button 
            variant="outline" 
            size="xl" 
            className="gap-3 bg-background text-foreground hover:bg-background/90"
            onClick={() => navigate("/auth")}
          >
            Sign Up Now
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>}

        {/* Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 border-t border-background/20 pt-12">
          {/* Company */}
          <div className="text-center md:text-left">
            <div className="mb-6 h-16 w-full flex items-center justify-center">
              <img 
                src={resolvedTheme === "dark" ? queueOSLogoDark : queueOSLogoLight} 
                alt="queueOS logo" 
                className="block h-16 w-auto object-contain" 
                loading="lazy" 
              />
            </div>
            <div className="flex items-center justify-center gap-4">
              <a 
                href="https://www.instagram.com/queue.os/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-background/70 hover:text-background transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/queueos/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-background/70 hover:text-background transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@queueOS" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-background/70 hover:text-background transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div className="text-center md:text-left">
            <h3 className="font-semibold mb-4">Product</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="#features" onClick={(e) => handleAnchorClick(e, "features")} className="text-background/70 hover:text-background transition-colors">Features</a></li>
              <li><a href="#pricing" onClick={(e) => handleAnchorClick(e, "pricing")} className="text-background/70 hover:text-background transition-colors">Pricing</a></li>
              <li><button onClick={() => { navigate("/whyqueueos"); window.scrollTo(0, 0); }} className="text-background/70 hover:text-background transition-colors">Why queueOS?</button></li>
              <li><button onClick={() => { navigate("/student"); window.scrollTo(0, 0); }} className="text-background/70 hover:text-background transition-colors">Are you a student?</button></li>
            </ul>
          </div>

          {/* Company */}
          <div className="text-center md:text-left">
            <h3 className="font-semibold mb-4">Company</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="https://help.queueos.com/collection/changelog" target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background transition-colors">Changelog</a></li>
              <li><a href="https://queueos.breezy.hr/" target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background transition-colors">Careers</a></li>
              <li><a href="https://queueos.com/forms/contact-us" target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Resources */}
          <div className="text-center md:text-left">
            <h3 className="font-semibold mb-4">Resources</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="https://help.queueos.com" target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background transition-colors">Help Center</a></li>
              <li><a href="https://affiliate.queueos.com" target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background transition-colors">Affiliate Program</a></li>
              <li><button onClick={() => navigate("/privacy-policy")} className="text-background/70 hover:text-background transition-colors">Privacy</button></li>
              <li><button onClick={() => navigate("/terms-of-service")} className="text-background/70 hover:text-background transition-colors">Terms</button></li>
              <li><a href="https://status.queueos.com" target="_blank" rel="noopener noreferrer" className="text-background/70 hover:text-background transition-colors">Status</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-background/20 mt-12 pt-8">
          <div className="text-background/60 text-sm text-center">
            <span>2025 queueOS.</span>
          </div>
        </div>
      </div>
    </footer>;
};